import React, { useEffect, useState } from "react";
import { Button, ConfigProvider, Drawer, InputNumber, Modal, Slider } from "antd";
import SheetHeaderClose from "./SheetHeaderClose";
import {
  BUDGET_MAX,
  BUDGET_MIN,
  BUDGET_STEP,
  DURATION_FALLBACK_MAX,
  DURATION_ANY,
  POPULAR_DURATIONS,
  TOUR_TYPE_LABEL,
  GroundFilterState,
  GroundTourType,
  toggleValue,
} from "../../../../utils/groundListingParams";
import type { LandTourFilterOptions } from "../../../../interfaces/land-tour.interface";

interface Props {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  filters: GroundFilterState;
  /** meta.filters from the listing response; absent until the first load lands. */
  options?: LandTourFilterOptions;
  onApply: (next: GroundFilterState) => void;
}

const formatRinggit = (value?: number) => `RM ${Number(value ?? 0).toLocaleString("en-US")}`;

const parseRinggit = (value?: string) => Number((value || "").replace(/[^\d]/g, "")) || 0;

const SLIDER_THEME = {
  components: {
    Slider: {
      railSize: 4,
      railBg: "#E2E5EB",
      railHoverBg: "#E2E5EB",
      trackBg: "#0062E3",
      trackHoverBg: "#0062E3",
      handleSize: 20,
      handleSizeHover: 20,
      handleLineWidth: 2,
      handleLineWidthHover: 2,
    },
  },
};

// meta.filters.guide_languages is the raw vocabulary ("English", "Mandarin",
// "English - Mandarin", "Not Applicable"); the design labels the chips
// "English Guided" / "Mandarin Guided".
const languageLabel = (language: string) =>
  language === "Not Applicable" ? language : `${language} Guided`;

export default function GroundFiltersPanel({
  open,
  onClose,
  isMobile,
  filters,
  options,
  onApply,
}: Props) {
  const [draft, setDraft] = useState<GroundFilterState>(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const durationMax = Math.max(options?.maxDuration || DURATION_FALLBACK_MAX, 1);
  const duration = draft.duration ?? DURATION_ANY;
  const budget = draft.budget ?? [BUDGET_MIN, BUDGET_MAX];

  // Only the popular chips the catalogue can actually satisfy.
  const durationChips = POPULAR_DURATIONS.filter((days) => days <= durationMax);

  const setBudget = (next: [number, number]) => setDraft({ ...draft, budget: next });

  const clearAll = () =>
    setDraft({
      ...draft,
      tourTypes: [],
      languages: [],
      inclusions: [],
      promotions: [],
      duration: null,
      budget: null,
    });

  const apply = () => {
    onApply(draft);
    onClose();
  };

  const chipSection = (
    title: string,
    chips: { value: string; label: string }[],
    selected: string[],
    onToggle: (value: string) => void,
    modifier = ""
  ) =>
    chips.length > 0 && (
      <section className="v2-filters__section">
        <h3 className="v2-filters__title">{title}</h3>
        <div className="v2-filters__chips">
          {chips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              className={`v2-chip ${modifier} ${selected.includes(chip.value) ? "is-active" : ""}`.replace(/\s+/g, " ")}
              onClick={() => onToggle(chip.value)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </section>
    );

  const body = (
    <div className="v2-filters">
      {chipSection(
        "Tour Type",
        (["private", "share"] as GroundTourType[]).map((type) => ({
          value: type,
          label: TOUR_TYPE_LABEL[type],
        })),
        draft.tourTypes,
        (value) =>
          setDraft({ ...draft, tourTypes: toggleValue(draft.tourTypes, value as GroundTourType) })
      )}

      {chipSection(
        "Guided Language",
        (options?.guideLanguages || []).map((language) => ({
          value: language,
          label: languageLabel(language),
        })),
        draft.languages,
        (value) => setDraft({ ...draft, languages: toggleValue(draft.languages, value) })
      )}

      {chipSection("Inclusion", options?.inclusions || [], draft.inclusions, (value) =>
        setDraft({ ...draft, inclusions: toggleValue(draft.inclusions, value) })
      )}

      {chipSection(
        "Promotions",
        (options?.promotions || []).map((tag) => ({ value: tag.id, label: tag.name })),
        draft.promotions,
        (value) => setDraft({ ...draft, promotions: toggleValue(draft.promotions, value) }),
        "v2-chip--promo"
      )}

      <section className="v2-filters__section">
        <h3 className="v2-filters__title">Trip Duration</h3>
        <div className="v2-filters__slider">
          <span className="v2-filters__slider-value">{duration === DURATION_ANY ? "All" : `${duration} Days`}</span>
          <ConfigProvider theme={SLIDER_THEME}>
            <Slider
              min={DURATION_ANY}
              max={durationMax}
              value={duration}
              tooltip={{ open: false }}
              onChange={(value: number) =>
                setDraft({ ...draft, duration: value === DURATION_ANY ? null : value })
              }
            />
          </ConfigProvider>
        </div>
        <div className="v2-filters__chips">
          {durationChips.map((days) => (
            <button
              key={days}
              type="button"
              className={`v2-chip ${draft.duration === days ? "is-active" : ""}`}
              onClick={() => setDraft({ ...draft, duration: draft.duration === days ? null : days })}
            >
              {days} Days
            </button>
          ))}
        </div>
      </section>

      <section className="v2-filters__section">
        <h3 className="v2-filters__title">Budget per traveler</h3>
        <div className="v2-filters__slider">
          <ConfigProvider theme={SLIDER_THEME}>
            <Slider
              range
              min={0}
              max={BUDGET_MAX}
              step={BUDGET_STEP}
              value={budget}
              tooltip={{ open: false }}
              onChange={(value: number[]) =>
                setBudget([Math.max(value[0], BUDGET_MIN), Math.max(value[1], BUDGET_MIN)])
              }
            />
          </ConfigProvider>
        </div>
        <div className="v2-filters__range">
          <label className="v2-filters__range-field">
            <span>Minimum</span>
            <InputNumber
              min={BUDGET_MIN}
              max={budget[1]}
              step={BUDGET_STEP}
              value={budget[0]}
              formatter={formatRinggit}
              parser={parseRinggit}
              onChange={(value) => setBudget([Number(value ?? BUDGET_MIN), budget[1]])}
            />
          </label>
          <label className="v2-filters__range-field">
            <span>Maximum</span>
            <InputNumber
              min={budget[0]}
              max={BUDGET_MAX}
              step={BUDGET_STEP}
              value={budget[1]}
              formatter={formatRinggit}
              parser={parseRinggit}
              onChange={(value) => setBudget([budget[0], Number(value ?? BUDGET_MAX)])}
            />
          </label>
        </div>
      </section>
    </div>
  );

  const footer = (
    <div className="v2-filters__footer">
      <Button type="text" className="v2-filters__clear" onClick={clearAll}>
        {isMobile ? "Clear All" : "Clear"}
      </Button>
      <Button type="primary" className="v2-filters__apply" onClick={apply}>
        {isMobile ? "Apply" : "Show Results"}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        placement="bottom"
        height="auto"
        title="Filters"
        closable={false}
        extra={<SheetHeaderClose onClose={onClose} />}
        rootClassName="v2-sheet v2-sheet--filters"
        footer={footer}
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Filters"
      width={720}
      centered
      rootClassName="v2-filters-panel v2-ground-filters"
      footer={footer}
    >
      {body}
    </Modal>
  );
}
